/**
 * useEmitEvent — fire-and-forget SessionEvent emitter (Bible §3.0 rule 7).
 *
 * Per the wiring rules, mutations in UI must emit a SessionEvent but MUST NOT
 * be awaited on a UI-critical path.  This hook returns a function that swallows
 * errors silently (they surface via TelemetryContext in a later wave).
 */
import { useCallback } from 'react';
import type {
  SessionEventPayload,
  SessionEventCategory,
  Severity,
} from '@retail-ops/shared-types';
import { useApiClient } from '@/contexts/ApiClientContext';
import { useAuth } from '@/contexts/AuthContext';

export interface EmitEventInput {
  eventType: string;
  eventCategory: SessionEventCategory;
  severity?: Severity;
  cartId?: string;
  sessionId?: string;
  context?: string;
  payload?: Record<string, unknown>;
}

export function useEmitEvent(): (input: EmitEventInput) => void {
  const client = useApiClient();
  const { user } = useAuth();

  return useCallback((input: EmitEventInput): void => {
    // `timestamp` is the branded `Iso8601` type in shared-types; cast the
    // JS-native ISO string explicitly.  api-client does not re-validate it.
    const payload: SessionEventPayload = {
      eventType: input.eventType,
      eventCategory: input.eventCategory,
      severity: input.severity ?? 'info',
      ...(input.cartId ? { cartId: input.cartId } : {}),
      ...(input.sessionId ? { sessionId: input.sessionId } : {}),
      ...(user?.upn ? { actor: user.upn } : {}),
      ...(input.context ? { context: input.context } : {}),
      ...(input.payload ? { payloadJson: JSON.stringify(input.payload) } : {}),
      timestamp: new Date().toISOString() as SessionEventPayload['timestamp'],
    };

    // Fire-and-forget — never await in UI paths (Rule #8).
    client.retail.events.write(payload).catch(() => {
      // TODO: route into TelemetryContext once Track A exposes a sink.
    });
  }, [client, user]);
}
